import { Spinner } from '@Common/Spinner';
import { CheckIcon } from '@heroicons/react/24/outline';
import { SetStateType } from 'commonTypes';
import { classNames } from 'helpers/validation';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { useGetPrices } from 'store/subscription/actions';
import {
  SubscriptionCustomerType,
  SubscriptionPriceType,
} from 'store/subscription/types';

interface ChooseUpdateSubscriptionPriceProps {
  setChosenPrice: SetStateType<SubscriptionPriceType | null>;
  chosenPrice: SubscriptionPriceType | null;
  active: string;
  subscription?: SubscriptionCustomerType;
  modalClose: () => void;
  setOpenCancelSubscriptionModal: SetStateType<boolean>;
}

export const ChooseUpdateSubscriptionPrice = ({
  setChosenPrice,
  chosenPrice,
  active,
  subscription,
  modalClose,
  setOpenCancelSubscriptionModal,
}: ChooseUpdateSubscriptionPriceProps) => {
  const { data: prices, isPending } = useGetPrices();

  return (
    <>
      {isPending ? (
        <Spinner color="bg-primary" />
      ) : (
        prices && (
          <div className="mx-auto max-w-6xl px-6 lg:px-4">
            <div className="isolate mx-auto mt-5 grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
              {prices
                .filter((price) => price.product_name.toLowerCase() === 'menu')
                .map((tierPrice) => (
                  <div
                    key={'tier-price-' + tierPrice.price_id}
                    className={classNames(
                      tierPrice.price_id === chosenPrice?.price_id
                        ? 'ring-2 ring-indigo-600'
                        : tierPrice.price_id === active
                          ? 'ring-2 ring-indigo-300'
                          : 'ring-1 ring-gray-200',
                      'rounded-3xl p-8 xl:p-10 relative',
                    )}
                  >
                    {tierPrice.price_id === active && (
                      <span className="absolute top-5 right-4 inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset bg-indigo-50 text-indigo-700 ring-indigo-700/10">
                        <FormattedMessage id="yourCurrentPlan" />
                      </span>
                    )}
                    <div className="flex items-center justify-between gap-x-4">
                      <h3
                        className={classNames(
                          tierPrice.price_id === chosenPrice?.price_id
                            ? 'text-indigo-600'
                            : 'text-gray-500',
                          'text-lg leading-8',
                        )}
                      >
                        {tierPrice.nickname}
                      </h3>
                    </div>
                    <p className="mt-4 text-sm leading-6 text-gray-600">
                      {tierPrice.product_name}
                    </p>
                    <p className="mt-6 flex items-baseline gap-x-1">
                      <span className="text-4xl font-bold tracking-tight text-gray-900">
                        <FormattedNumber
                          style="currency"
                          currency={tierPrice.currency.toUpperCase()}
                          value={+tierPrice.unit_amount / 100 / tierPrice.interval_count}
                          maximumFractionDigits={0}
                        />
                      </span>
                      <span className="text-sm font-semibold leading-6 text-gray-600">
                        / {tierPrice.interval}
                      </span>
                    </p>
                    <button
                      className={classNames(
                        tierPrice.price_id === chosenPrice?.price_id
                          ? 'bg-indigo-600 text-white shadow-sm hover:bg-indigo-500'
                          : 'text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:ring-indigo-300',
                        'mt-6 w-full block rounded-md py-2 px-3 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600',
                      )}
                      disabled={tierPrice.price_id === active}
                      onClick={() => {
                        if (tierPrice.price_id !== active) {
                          setChosenPrice(tierPrice);
                        }
                      }}
                    >
                      <FormattedMessage
                        id={active === tierPrice.price_id ? 'yourCurrentPlan' : 'choose'}
                      />
                    </button>
                    {active === tierPrice.price_id &&
                      subscription?.status.toLocaleLowerCase() !== 'cancel' &&
                      !subscription?.cancel_at_period_end && (
                        <div className="flex items-center justify-center mt-1">
                          <button
                            className="text-red-300 text-sm"
                            onClick={() => {
                              modalClose();
                              setOpenCancelSubscriptionModal(true);
                            }}
                          >
                            <FormattedMessage id="cancelSubscription" />
                          </button>
                        </div>
                      )}
                    <ul className="mt-6 space-y-3 text-sm leading-6 text-gray-600 xl:mt-10">
                      {[
                        'multilingual_support',
                        'menu_updates',
                        'customizable_appearance',
                        'ai_powered_item_creation',
                        'extensive_item_options',
                        'visual_enhancement',
                        'seamless_review_integration',
                      ].map((feature) => (
                        <li key={feature} className="flex gap-x-3">
                          <CheckIcon
                            className="h-6 w-5 flex-none text-indigo-600"
                            aria-hidden="true"
                          />
                          <FormattedMessage id={feature} />
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
            </div>
          </div>
        )
      )}
    </>
  );
};
